/**
 * Display rows for the five-aṅga table from the API's structured `angas`
 * (B-139-WEB, docs/SHARED_CONTRACT_SPEC.md). No display string is parsed.
 *
 * The rows show the same segments the server sentence shows, so the English card
 * is unchanged: every segment with an end gets "till HH:MM" (hours past 24 kept, as
 * the sentence does: 25:10 = 01:10 next day), and the open successor is shown only
 * where the sentence says "afterwards": after a single end before midnight, and as
 * the third karaṇa.
 */
import type { components } from '../../types/api.gen';

type AngaSegment = components['schemas']['AngaSegment'];

export type AngaKind = 'tithi' | 'nakshatra' | 'yoga' | 'karana';

export interface AngaRow {
  name: string;
  /** "HH:MM" in hours since local midnight of the shown date (may be >= 24); null = open. */
  time: string | null;
  number: number;
  nameEn: string;
}

/** Hours since local midnight of `day` ("YYYY-MM-DD") for an ISO `ends_at` in the location's offset. */
/**
 * Indic digits to ASCII. The API's `date` arrives with Devanagari digits for `sa`
 * ("२०२६-०२-२२"), a display conversion leaking into a data field, while `ends_at` stays
 * ASCII. Date arithmetic on the raw field gave "NaN:10" on every Sanskrit aṅga time
 * (B-139-SADATE, LESSONS_LEARNED 2026-10-12). Devanagari, Telugu and Kannada ranges.
 */
export const asciiDigits = (s: string): string =>
  s.replace(/[\u0966-\u096F\u0C66-\u0C6F\u0CE6-\u0CEF]/g, d => {
    const c = d.charCodeAt(0);
    const zero = c >= 0x0CE6 ? 0x0CE6 : c >= 0x0C66 ? 0x0C66 : 0x0966;
    return String(c - zero);
  });

export const clockSince = (endsAt: string, day: string): string => {
  const endDay = endsAt.slice(0, 10);
  const hh = parseInt(endsAt.slice(11, 13), 10);
  const mm = endsAt.slice(14, 16);
  const shown = Date.parse(`${asciiDigits(day)}T00:00:00Z`);
  // An unreadable `day` must never print NaN: fall back to the clock on the end's own date.
  const days = Number.isNaN(shown) ? 0 : Math.round((Date.parse(`${endDay}T00:00:00Z`) - shown) / 86400000);
  return `${String(hh + 24 * days).padStart(2, '0')}:${mm}`;
};

export const angaRows = (segments: AngaSegment[], kind: AngaKind, day: string): AngaRow[] => {
  const row = (s: AngaSegment, withTime: boolean): AngaRow => ({
    name: s.name,
    time: withTime && s.ends_at ? clockSince(s.ends_at, day) : null,
    number: s.number,
    nameEn: s.name_en,
  });
  if (kind === 'karana') {
    return segments.slice(0, 3).map((s, i) => row(s, i < 2));
  }
  const finite = segments.filter(s => s.ends_at);
  const rows = finite.map(s => row(s, true));
  if (finite.length === 1) {
    const first = rows[0];
    const successor = segments[1];
    if (first.time && parseInt(first.time.slice(0, 2), 10) < 24 && successor) rows.push(row(successor, false));
  }
  return rows;
};
